import {shiftDate, shortDate} from '../../../src/utils/dates.ts';
import {getClientId} from '../../lib/storage.ts';
import {ContentSourceEdition} from '../../models/content-source-edition.ts';
import type {ReaderEdition} from '../../models/reader-edition.ts';

export interface AppMenuState {
	mode: string;
	navigationModel: string;
	design: string;
	font: string;
	showDone: string;
}

export function renderAppMenu(
	appMenuEl: HTMLElement,
	readerEdition: ReaderEdition,
	resourceType: string,
	state: AppMenuState
): void {
	const {mode, navigationModel, design, font, showDone} = state;
	const checked = (condition: boolean) => (condition ? 'checked' : '');

	appMenuEl.innerHTML = `
<div class="app-menu__close" data-action="toggle-menu" data-action-value="hide">×</div>
<div class="app-menu__columns">
	<section class="app-menu__column">
		<h2 class="app-menu__section-title">Subscriber</h2>
		<div class="app-menu__link-group">
			<span class="app-menu__group-label">Navigate</span>
			${renderSubscriberLinks(readerEdition, resourceType)}
		</div>
		${renderPreviousEditions(readerEdition)}
		<p class="app-menu__help-text">Navigation for subscribers. Editorial and Developer sections are hidden for subscribers.</p>
	</section>
	<section class="app-menu__column">
		<h2 class="app-menu__section-title">Editorial</h2>
		<div class="app-menu__nav-links">
			<a href="/admin" data-action="open-admin">Admin</a>
			${renderDrEditionLink(readerEdition)}
		</div>
		<p class="app-menu__help-text">Tools for editors to manage content and settings. Hidden for subscribers.</p>
	</section>
	<section class="app-menu__column">
		<h2 class="app-menu__section-title">Developer</h2>
		<div class="app-menu__settings">
			<fieldset class="app-menu__fieldset">
				<legend>Navigation</legend>
				<label><input type="radio" name="navigation-model" data-action="set-navigation-model" data-action-value="horizontal-scroll" ${checked(navigationModel === 'horizontal-scroll')}> Horizontal Scroll</label>
				<label><input type="radio" name="navigation-model" data-action="set-navigation-model" data-action-value="page-navigation" ${checked(navigationModel === 'page-navigation')}> Page Navigation</label>
			</fieldset>
			<fieldset class="app-menu__fieldset">
				<legend>Rendering</legend>
				<label><input type="radio" name="mode" data-action="set-mode" data-action-value="default" ${checked(mode === 'default')}> Default</label>
				<label><input type="radio" name="mode" data-action="set-mode" data-action-value="pressflow" ${checked(mode === 'pressflow')}> Pressflow</label>
			</fieldset>
			<fieldset class="app-menu__fieldset">
				<legend>Design</legend>
				<label><input type="radio" name="design" data-action="set-design" data-action-value="gunnar" ${checked(design === 'gunnar')}> Gunnar</label>
				<label><input type="radio" name="design" data-action="set-design" data-action-value="geir" ${checked(design === 'geir')}> Geir</label>
			</fieldset>
			<fieldset class="app-menu__fieldset">
				<legend>Typography</legend>
				<label><input type="radio" name="font" data-action="set-font" data-action-value="serif" ${checked(font === 'serif')}> Default Serif</label>
				<label><input type="radio" name="font" data-action="set-font" data-action-value="sans-serif" ${checked(font === 'sans-serif')}> Default Sans Serif</label>
				<label><input type="radio" name="font" data-action="set-font" data-action-value="customer-serif" ${checked(font === 'customer-serif')}> Custom Serif</label>
				<label><input type="radio" name="font" data-action="set-font" data-action-value="customer-sans-serif" ${checked(font === 'customer-sans-serif')}> Custom Sans Serif</label>
			</fieldset>
			<fieldset class="app-menu__fieldset">
				<legend>Articles</legend>
				<label><input type="checkbox" data-action="toggle-show-done" ${checked(showDone === 'hide')}> Hide read articles</label>
				<button class="app-menu__reset-btn" data-action="reset-state">Reset read states</button>
			</fieldset>
		</div>
		<p class="app-menu__help-text">Debug and testing options for developers. Hidden for subscribers and editors.</p>
	</section>
</div>
`;
}

function renderSubscriberLinks(readerEdition: ReaderEdition, resourceType: string): string {
	if (readerEdition.editionType === 'print-edition') {
		const editionUrl = `https://${getClientId()}.dredition.aptoma.no/#/products/${readerEdition.productId}/editions/${readerEdition.id}/print`;
		return `
		<a href="/home">Home</a>
		<a href="/print-products/${readerEdition.productName}">Editions</a>
		<a target="dredition" href="${editionUrl}">${shortDate(readerEdition.publishDate, readerEdition.locale)}</a>`;
	}

	if (['magazine', 'frontpage'].includes(readerEdition.productType)) {
		const editionUrl = `https://${getClientId()}.dredition.aptoma.no/#/products/${readerEdition.productId}/editions/${readerEdition.id}/${readerEdition.productType}`;
		return `
		<a href="/home">Home</a>
		<a href="/digital-products/${readerEdition.productName}">${readerEdition.productTitle}</a>
		<a target="dredition" href="${editionUrl}">${readerEdition.title}</a>`;
	}

	if (readerEdition instanceof ContentSourceEdition) {
		return `
		<a href="/home">Home</a>
		<a href="/feed/${readerEdition.sourceId}">${readerEdition.contentSource.attributes.title}</a>`;
	}

	if (resourceType === 'article') {
		return `
		<a href="/home">Home</a>
		<span>${readerEdition.title}</span>`;
	}

	return `
		<a href="/home">Home</a>
		<span>${readerEdition.productTitle}</span>
		<span>${readerEdition.title}</span>`;
}

function renderPreviousEditions(readerEdition: ReaderEdition): string {
	if (readerEdition.productType === 'print' && readerEdition.latestEditions?.length) {
		const links = readerEdition.latestEditions
			.toReversed()
			.filter((edition) => edition._id !== readerEdition.id)
			.slice(0, 7)
			.map(
				(edition) =>
					`<a href="/reader/${edition._id}">${shortDate(edition.publishDate, readerEdition.locale)}</a>`
			)
			.join('\n');
		return links
			? `<div class="app-menu__previous-editions"><span class="app-menu__previous-label">Previous editions</span>\n${links}</div>`
			: '';
	}

	if (readerEdition instanceof ContentSourceEdition) {
		const links = [];
		for (let offset = -1; offset >= -7; offset--) {
			const date = shiftDate(new Date(readerEdition.publishDate), offset);
			links.push(`<a href="${readerEdition.getUrl(date)}">${shortDate(date, readerEdition.locale)}</a>`);
		}
		return `<div class="app-menu__previous-editions"><span class="app-menu__previous-label">Previous editions</span>\n${links.join('\n')}</div>`;
	}

	return '';
}

function renderDrEditionLink(readerEdition: ReaderEdition): string {
	const clientId = getClientId();
	if (readerEdition.editionType === 'print-edition') {
		return `<a target="dredition" href="https://${clientId}.dredition.aptoma.no/#/products/${readerEdition.productId}/editions/${readerEdition.id}/print">Edit in DrEdition</a>`;
	}
	if (['magazine', 'frontpage'].includes(readerEdition.productType)) {
		return `<a target="dredition" href="https://${clientId}.dredition.aptoma.no/#/products/${readerEdition.productId}/editions/${readerEdition.id}/${readerEdition.productType}">Edit in DrEdition</a>`;
	}
	if (readerEdition instanceof ContentSourceEdition) {
		return `<a target="dredition" href="https://${clientId}.dredition.aptoma.no/#/settings/content-sources/${readerEdition.sourceId}/queries/${readerEdition.queryId}">DrEdition: ${readerEdition.query?.title}</a>`;
	}
	return '';
}
